import { TF } from 'react-translator-component';

import {
  Connection,
  ContractDirection,
  ContractStatus,
  ContractType,
  InviteDirection,
} from '../../../types/responses/Interoperability';

/**
 * Regras de contrato que a aba Conexões desenha (MV2-1713).
 *
 * Ficam separadas do componente porque são regra de negócio, não layout — e
 * porque a mesma regra decide o texto E o estado do controle.
 */

/** Contrato que chegou a valer: já foi aceito, mesmo que esteja pausado. */
export const isEffective = (status: ContractStatus): boolean =>
  status === 'ACTIVE' || status === 'SUSPENDED';

/**
 * O toggle de recebimento só existe quando há o que exibir.
 *
 * Exige contrato efetivado e que EU receba algo: bidirecional ou unilateral no sentido
 * `RECEIVING`. Em convite pendente ou revogado não há nada compartilhado.
 *
 * Era `=== 'BIDIRECTIONAL'`, de quando o campo tinha dois valores. Quando ele passou a ter três
 * (SHARING, RECEIVING, BIDIRECTIONAL), o contrato unilateral **recebido** passou a cair no lado
 * errado: a linha dizia "não recebe" com as estações recebidas ao lado, e o toggle ficava
 * desabilitado para sempre — o operador não conseguia esconder as estações daquele parceiro. O
 * backend nunca teve essa restrição: `setContractReceivedVisible` troca `visible_in_app` em
 * qualquer sentido.
 */
export const canToggleReceiving = (connection: Connection): boolean =>
  isEffective(connection.status) && connection.contract_type !== 'SHARING';

/**
 * Texto da coluna "estações que eu recebo", conforme o estado do contrato.
 *
 * Passa pelo TF, e nao devolve portugues cru: estas funcoes alimentam colunas
 * renderizadas sem T(), entao o texto fixo aqui saia em portugues nas outras quatro
 * linguas. O TF interpola {0} e devolve string, que e o que a coluna espera.
 */
export const receivingLabel = (connection: Connection): string => {
  if (!isEffective(connection.status)) {
    return connection.status === 'PENDING'
      ? TF('— awaiting acceptance')
      : TF('— contract ended');
  }

  // Só quem não recebe é o unilateral de saída. O unilateral de entrada recebe, e é o caso
  // que a comparação antiga com BIDIRECTIONAL punha do lado errado.
  if (connection.contract_type === 'SHARING') {
    return TF('does not receive — one-way contract');
  }

  return connection.received_stations_visible
    ? TF('{0} stations shown in my app', connection.received_station_count)
    : TF('{0} stations received · hidden in the app', connection.received_station_count);
};

/** Texto da coluna "estações que eu compartilho". */
export const sharingLabel = (connection: Connection): string => {
  if (connection.status === 'PENDING') {
    return TF('{0} in the invite · awaiting acceptance', connection.shared_station_count);
  }

  if (!isEffective(connection.status)) {
    return TF('0 · contract ended');
  }

  const accounts = connection.shared_account_count;

  return accounts === 1
    ? TF('stations · {0} account', accounts)
    : TF('stations · {0} accounts', accounts);
};

export const STATUS_STYLE: Record<ContractStatus, { bg: string; fg: string; dot: string; label: string }> = {
  ACTIVE: { bg: '#e7f6ed', fg: '#1e7a45', dot: '#26a35c', label: 'Ativo' },
  PENDING: { bg: '#fff4e0', fg: '#8a5300', dot: '#e08c00', label: 'Convite pendente' },
  SUSPENDED: { bg: '#fdeee4', fg: '#a8500f', dot: '#e0700f', label: 'Suspenso' },
  REVOKED: { bg: '#fdecec', fg: '#b02525', dot: '#c8352f', label: 'Revogado' },
};

/** Iniciais do parceiro para o quadrado colorido da primeira coluna. */
export const initialsOf = (name: string): string =>
  (name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase();

/**
 * A seta do tipo de contrato, do ponto de vista de quem olha a tela.
 *
 * `←` a estação entra (recebo do parceiro), `→` a estação sai (compartilho com ele), `⇄` os
 * dois. A convenção é uma só em todo o módulo: a seta descreve para onde a estação vai em
 * relação a mim, e não para quem passa a exibir.
 */
export const DIRECTION_ARROW: Record<ContractDirection, string> = {
  RECEIVING: '←',
  SHARING: '→',
  BIDIRECTIONAL: '⇄',
};

/** O rótulo do tipo, com a seta. */
export const directionLabel = (direction: ContractDirection): string =>
  `${DIRECTION_ARROW[direction]} ${
    direction === 'BIDIRECTIONAL' ? TF('Bidirectional') : TF('One-way')
  }`;

/**
 * O que a seta quer dizer, em palavras.
 *
 * Existe porque seta sozinha só é óbvia para quem já conhece a convenção — e esta tela é lida
 * por quem está aprendendo o módulo.
 */
export const directionHint = (direction: ContractDirection): string => {
  if (direction === 'BIDIRECTIONAL') {
    return TF('Both apps display the other stations');
  }

  return direction === 'RECEIVING'
    ? TF('Only your app displays the partner stations')
    : TF('Only the partner app displays your stations');
};

/**
 * O tipo de um convite, lido do ponto de vista de quem olha.
 *
 * O convite grava dois valores, porque quem gera escolhe entre "só as minhas vão" e "as duas
 * vão". Mas a mesma linha significa o oposto para cada lado: um convite unilateral que eu
 * **enviei** vai me fazer compartilhar; o mesmo convite **recebido** vai me fazer receber.
 *
 * É essa assimetria que obriga a listagem a ter três valores e o convite a ter dois.
 */
export const directionOfInvite = (
  contractType: ContractType,
  direction: InviteDirection,
): ContractDirection => {
  if (contractType === 'BIDIRECTIONAL') {
    return 'BIDIRECTIONAL';
  }

  return direction === 'SENT' ? 'SHARING' : 'RECEIVING';
};

/**
 * O que suspender vai esconder, e de quem (MV2-1809).
 *
 * Suspender é reversível, mas tira estações do ar na hora — e o texto depende do
 * sentido, porque a ação vale para o contrato inteiro. No unilateral de saída
 * somem as minhas estações no app do parceiro; no de entrada somem as dele no
 * meu; no bidirecional, as duas coisas.
 *
 * Um texto único falando só das minhas estaria errado justamente para quem
 * recebe — que é quem menos imagina estar mexendo no próprio app ao suspender.
 *
 * Devolve string, e não JSX: alimenta o `content` do Modal.confirm.
 */
export const suspendWarning = (connection: Connection): string => {
  const partner = connection.partner_name || TF('the partner');

  if (connection.contract_type === 'SHARING') {
    return TF(
      'Suspending hides your {0} stations in the app of {1} until you reactivate.',
      connection.shared_station_count,
      partner,
    );
  }

  if (connection.contract_type === 'RECEIVING') {
    return TF(
      'Suspending hides the {0} stations of {1} in your app until you reactivate.',
      connection.received_station_count,
      partner,
    );
  }

  return TF(
    'Suspending hides your {0} stations in the app of {1}, and the {2} stations of theirs in your app, until you reactivate.',
    connection.shared_station_count,
    partner,
    connection.received_station_count,
  );
};
